import { Card, Status as StatusComponent } from '@statsbomb/kitbag-components';
import { ReactNode } from 'react';
import { useStatus } from '@/hooks/useStatus';
import { StatusTranslationKey, StatusType } from '@/types/status';
import { NoTextLoader } from '../contentState/NoTextLoader';
import { FallbackSideNav } from '../navigation/fallback/FallbackSideNav';
import { ContactSupportStatusDescription } from '../support/ContactSupportCard';
import { EmptyTopBar, TopBar } from '../topBar/TopBar';
import { AppShellContentsBase } from './AppShell';

const AppShellFallbackBase = ({
  statusDescription,
  statusTitle,
  statusType,
  topBar = <TopBar />,
  children,
}: {
  statusDescription: ReactNode;
  statusTitle: string;
  statusType: StatusType;
  topBar?: ReactNode;
  children?: ReactNode;
}) => (
  <AppShellContentsBase topBar={topBar}>
    <div className="w-full h-full p-2">
      <Card>
        <StatusComponent type={statusType} title={statusTitle}>
          {statusDescription}
        </StatusComponent>
      </Card>
    </div>
    {children}
  </AppShellContentsBase>
);

const AppShellFallback = ({
  topBar,
  status,
  children,
}: {
  topBar?: ReactNode;
  status: StatusTranslationKey;
  children?: ReactNode;
}) => {
  const statusProps = useStatus('appShell', status, {
    [StatusTranslationKey.ERROR]: <ContactSupportStatusDescription />,
  });

  if (!statusProps.statusType) return null;

  return (
    <AppShellFallbackBase topBar={topBar} {...statusProps}>
      {children}
    </AppShellFallbackBase>
  );
};

/**
 * Loading fallback for use inside an already-rendered app shell (e.g. in AppShell's
 * outlet). Fills the content area only — rendering AppShellNoTextLoadingFallback there
 * would nest a second side nav next to the real one.
 */
export const AppShellContentsNoTextLoadingFallback = ({ children }: { children?: ReactNode }) => (
  <>
    <AppShellContentsBase topBar={<EmptyTopBar />}>
      <NoTextLoader />
    </AppShellContentsBase>
    {children}
  </>
);

export const AppShellNoTextLoadingFallback = ({ children }: { children?: ReactNode }) => (
  <div className="flex h-full isolate" data-testid="appshell">
    <div className="z-10">
      <FallbackSideNav />
    </div>
    <AppShellContentsNoTextLoadingFallback>{children}</AppShellContentsNoTextLoadingFallback>
  </div>
);

export const AppShellErrorFallback = () => <AppShellFallback status={StatusTranslationKey.ERROR} />;
